import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChange, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
  input,
} from '@angular/core';

import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { billingPlanTokenRecord } from '@core/billing-util';
import type { IconToken } from '@core/user-interface-legacy';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { FeatureGuardFacade } from '../feature-guard.facade';
import type { FeatureGuardComponent } from '../feature-guard.types';
import { FeatureGuardMode, GuardedFeature } from '../feature-guard.types';

@Component({
  selector: 'rt-feature-guard-button',
  templateUrl: 'feature-guard-button.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ButtonComponent, IconComponent],
})
export class FeatureGuardButtonComponent implements FeatureGuardComponent {
  protected readonly cdRef = inject(ChangeDetectorRef);
  public readonly facade = inject(FeatureGuardFacade);

  public readonly forceEnabled = input<boolean>(undefined!);
  public readonly defaultBtnText = input<string>(undefined!);
  @Input({ required: true }) public feature: GuardedFeature;
  @Input() public mode: FeatureGuardMode = FeatureGuardMode.Dialog;
  @Input() public customTpl: TemplateRef<any>;

  public isFeatureAvailable: boolean = true;
  public colorToken: IconToken;
  public GuardedFeature: typeof GuardedFeature = GuardedFeature;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    const forceEnabled = this.forceEnabled();
    if (forceEnabled) {
      this.isFeatureAvailable = forceEnabled;
    } else {
      const featureAvailable$ = this.facade.checkFeatureAvailability(this.feature);
      const availableFromPlan$ = this.facade.getPlanWhereFeatureIsAvailable(this.feature);

      combineLatest([featureAvailable$, availableFromPlan$])
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(([isFeatureAvailable, availableFromPlan]) => {
          this.isFeatureAvailable = isFeatureAvailable;
          this.colorToken = billingPlanTokenRecord[availableFromPlan!];
          this.cdRef.detectChanges();
        });
    }
  }

  public ngOnChanges({ forceEnabled }: { forceEnabled: SimpleChange }) {
    if (forceEnabled) {
      this.isFeatureAvailable = this.forceEnabled();
      this.cdRef.detectChanges();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
